import { useRef, useState } from 'react'
import type { StyleReference } from '../types'
import { styleRefsApi } from '../lib/api'

interface StyleRefPickerProps {
  isOpen: boolean
  onClose: () => void
  styleRefs: StyleReference[]
  onSelect: (styleRef: StyleReference, base64?: string) => void
}

export function StyleRefPicker({
  isOpen,
  onClose,
  styleRefs,
  onSelect,
}: StyleRefPickerProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (!isOpen) return null

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be less than 5MB')
      return
    }

    setIsUploading(true)
    setUploadError(null)

    const reader = new FileReader()
    reader.onload = async () => {
      try {
        const base64 = reader.result as string
        const styleRef = await styleRefsApi.create({
          name: file.name.replace(/\.[^/.]+$/, ''),
          description: 'Uploaded style reference',
          imageBase64: base64,
        })
        onSelect(styleRef, base64)
      } catch (err: any) {
        setUploadError(err.message || 'Failed to upload style reference')
      } finally {
        setIsUploading(false)
      }
    }
    reader.onerror = () => {
      setUploadError('Failed to read image file')
      setIsUploading(false)
    }
    reader.readAsDataURL(file)
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-white rounded-2xl shadow-hover z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-color">
          <h3 className="font-serif text-xl font-semibold">Choose Style Reference</h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-soft"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Upload New */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full mb-4 p-4 border-2 border-dashed border-primary/30 rounded-xl bg-primary/5 hover:bg-primary/10 transition-colors flex items-center justify-center gap-3"
          >
            <span className="material-symbols-outlined text-primary">
              {isUploading ? 'hourglass_empty' : 'upload'}
            </span>
            <span className="font-medium text-primary-dark">
              {isUploading ? 'Uploading...' : 'Upload New Style'}
            </span>
          </button>

          {uploadError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
              {uploadError}
            </div>
          )}

          {/* Style Grid */}
          {styleRefs.length === 0 ? (
            <div className="text-center py-8 text-text-muted">
              <span className="material-symbols-outlined text-5xl mb-2 block">
                palette
              </span>
              <p>No style references yet.</p>
              <p className="text-sm">Upload one to get started!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {styleRefs.map((styleRef) => (
                <button
                  key={styleRef.id}
                  onClick={() => onSelect(styleRef)}
                  className="group relative aspect-square rounded-xl overflow-hidden border-2 border-transparent hover:border-primary transition-colors"
                >
                  <img
                    src={styleRef.thumbnailUrl ?? styleRef.imageUrl}
                    alt={styleRef.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <div className="text-left">
                      <p className="font-medium text-white text-sm">{styleRef.name}</p>
                      {styleRef.description && (
                        <p className="text-white/70 text-xs truncate">
                          {styleRef.description}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
